import React from 'react';
import { Instagram, Facebook, MapPin, Phone, Mail, Globe } from 'lucide-react';
import './Footer.css';
import { ReactComponent as ThreadsLogo } from '../assets/threads.svg';
import logo from '../assets/a5.webp';

const Footer = ({ className = '' }) => {
  return (
    <footer className={`bg-[#021810] text-gray-300 py-16 border-t border-amber-400/20 relative ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          
          {/* Company Info */}
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Sakae Studio" className="h-16 w-auto object-contain" />
            </div>
            <p className="text-xs leading-relaxed text-gray-400">
              <strong className="text-amber-400">Sakae Studio Private Limited</strong><br />
              Opulent Architecture | Precision Execution.<br />
              Bespoke interior and architectural solutions for luxury residences, commercial hubs, and urban developments.
            </p>
            <div className="flex items-center space-x-3 pt-2">
              <a 
                href="https://www.instagram.com/sakae.in/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2.5 bg-amber-400/10 border border-amber-400/30 rounded-full text-amber-400 hover:bg-amber-400 hover:text-gray-900 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://www.facebook.com/share/15VcHpxynD/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2.5 bg-amber-400/10 border border-amber-400/30 rounded-full text-amber-400 hover:bg-amber-400 hover:text-gray-900 transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="https://www.threads.net/@sakae.in"
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2.5 bg-amber-400/10 border border-amber-400/30 rounded-full text-amber-400 hover:bg-amber-400 hover:text-gray-900 transition-all duration-300 flex items-center justify-center"
                aria-label="Threads"
              >
                <ThreadsLogo fill="currentColor" width={18} height={18} />
              </a>
            </div>
          </div>

          {/* Our Services */}
          <div>
            <h4 className="font-serif text-xs uppercase tracking-widest text-amber-400 mb-5 pb-2 border-b border-amber-400/20">Our Services</h4>
            <ul className="space-y-2.5 text-xs text-gray-300">
              <li><a href="/services" className="hover:text-amber-400 transition-colors">Architectural Design & Execution</a></li>
              <li><a href="/services" className="hover:text-amber-400 transition-colors">Interior Design & Turnkey Solutions</a></li>
              <li><a href="/services" className="hover:text-amber-400 transition-colors">3D Visualization & VR Walkthroughs</a></li>
              <li><a href="/services" className="hover:text-amber-400 transition-colors">Project Management Consultancy</a></li>
              <li><a href="/services" className="hover:text-amber-400 transition-colors">Renovation & Landscape Planning</a></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-xs uppercase tracking-widest text-amber-400 mb-5 pb-2 border-b border-amber-400/20">Navigation</h4>
            <ul className="space-y-2.5 text-xs text-gray-300">
              <li><a href="/" className="hover:text-amber-400 transition-colors">Home</a></li>
              <li><a href="/portfolio" className="hover:text-amber-400 transition-colors">Portfolio Showcase</a></li>
              <li><a href="/blog" className="hover:text-amber-400 transition-colors">About Sakae Studio</a></li>
              <li><a href="/start-project" className="hover:text-amber-400 transition-colors">Start Your Project</a></li>
              <li><a href="/services" className="hover:text-amber-400 transition-colors">Technocrats Legacy</a></li>
            </ul>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="font-serif text-xs uppercase tracking-widest text-amber-400 mb-5 pb-2 border-b border-amber-400/20">Contact</h4>
            <ul className="space-y-3 text-xs text-gray-300">
              <li className="flex items-center gap-2">
                <Phone size={14} className="text-amber-400 shrink-0" />
                <span>+91 90001 15517</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} className="text-amber-400 shrink-0" />
                <span className="break-all">arsak9000@gmail.com</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} className="text-amber-400 shrink-0" />
                <span className="break-all">sakaestudio1@gmail.com</span>
              </li>
              <li className="flex items-center gap-2">
                <Globe size={14} className="text-amber-400 shrink-0" />
                <span>www.sakae.in</span>
              </li>
            </ul>
          </div>

          {/* Studios & Offices */}
          <div>
            <h4 className="font-serif text-xs uppercase tracking-widest text-amber-400 mb-5 pb-2 border-b border-amber-400/20 flex items-center gap-1.5">
              <MapPin size={14} /> Our Studios
            </h4>
            <div className="space-y-4 text-xs">
              <div className="bg-white/5 border border-amber-400/10 p-3 rounded-lg">
                <h5 className="font-semibold text-white mb-1">Banjara Hills Studio</h5>
                <p className="text-gray-400 leading-relaxed text-[11px]">
                  10-5-2/F/G/7,8,9, Sarda Sadan,<br />
                  Opp. Balaji Grand Bazaar, Road No.1,<br />
                  Banjara Hills, Hyderabad – 500034
                </p>
              </div>
              <div className="bg-white/5 border border-amber-400/10 p-3 rounded-lg">
                <h5 className="font-semibold text-white mb-1">Head Office</h5>
                <p className="text-gray-400 leading-relaxed text-[11px]">
                  Technocrats Residency,<br />
                  Vidyanagar, Hyderabad – 500044
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* Copyright Footer Bar */}
        <div className="border-t border-amber-400/10 mt-12 pt-8 text-center text-xs text-gray-500 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p>© {new Date().getFullYear()} Sakae Studio Private Limited. All rights reserved.</p>
          <p className="text-amber-400/70 font-mono text-[11px]">A Subsidiary of Technocrats Group • 34+ Years Legacy</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
